import type { ApiResponse, GetListResponse } from '@gokrew/models/generic';
import api from './client';
import type {
  CompanyTaskThread,
  CompanyTaskMessage,
  ChangeMyStatusDto,
  CreateMessageDto,
  UpdateMessageDto,
  FilterMyCompanyTasksDto,
  MyAdminTask,
  GetMyAdminTaskDetailResponse,
  AdminTasksSummary,
  CompanyTaskActivityLog,
} from '@gokrew/models/tasks';

const base = (companyId: string) => `/companies/${companyId}/admin/task-inbox`;

export async function getMyAdminTasks(
  companyId: string,
  params: FilterMyCompanyTasksDto = {}
) {
  return api.get<GetListResponse<MyAdminTask>>(base(companyId), { params });
}

export async function getMyAdminTasksSummary(companyId: string) {
  return api.get<ApiResponse<AdminTasksSummary>>(`${base(companyId)}/summary`);
}

export async function getMyAdminTaskById(
  companyId: string,
  assignmentId: string
) {
  return api.get<GetMyAdminTaskDetailResponse>(
    `${base(companyId)}/${assignmentId}`
  );
}

export async function updateMyAdminTaskStatus(
  companyId: string,
  assignmentId: string,
  payload: ChangeMyStatusDto
) {
  return api.patch(`${base(companyId)}/${assignmentId}/status`, payload);
}

export async function getMyAdminTaskActivity(
  companyId: string,
  assignmentId: string
) {
  return api.get<ApiResponse<CompanyTaskActivityLog[]>>(
    `${base(companyId)}/${assignmentId}/activity`
  );
}

// ─── Threads & Messages ───────────────────────────────────────

export async function getOrCreateAdminTaskThread(
  companyId: string,
  assignmentId: string
) {
  return api.post<ApiResponse<CompanyTaskThread>>(
    `${base(companyId)}/${assignmentId}/thread`
  );
}

export async function getAdminTaskThread(companyId: string, threadId: string) {
  return api.get<CompanyTaskThread>(`${base(companyId)}/threads/${threadId}`);
}

export async function getAdminTaskThreadMessages(
  companyId: string,
  threadId: string
) {
  return api.get<ApiResponse<CompanyTaskMessage[]>>(
    `${base(companyId)}/threads/${threadId}/messages`
  );
}

export async function postAdminTaskMessage(
  companyId: string,
  threadId: string,
  payload: CreateMessageDto
) {
  return api.post<CompanyTaskMessage>(
    `${base(companyId)}/threads/${threadId}/messages`,
    payload
  );
}

export async function editAdminTaskMessage(
  companyId: string,
  threadId: string,
  messageId: string,
  payload: UpdateMessageDto
) {
  return api.patch<CompanyTaskMessage>(
    `${base(companyId)}/threads/${threadId}/messages/${messageId}`,
    payload
  );
}

export async function deleteAdminTaskMessage(
  companyId: string,
  threadId: string,
  messageId: string
) {
  return api.delete(
    `${base(companyId)}/threads/${threadId}/messages/${messageId}`
  );
}
